<template>
  <component :is="currentComponent" :key="currentModuleName ?? ''" ref="activeModuleRef" />
</template>

<script setup lang="ts">
import {
  defineAsyncComponent,
  type Component,
  type ComponentPublicInstance,
} from "vue";
import { getRevealPayloadFromRoute, getVueModuleForRouteName } from "@wkt/router/moduleRegistry";
import { useShellStore } from "@wkt/stores/shell";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const shellStore = useShellStore();
const activeModuleRef = ref<ComponentPublicInstance | null>(null);
let lastRevealSignature: string | null = null;

function loadModule(
  loader: () => Promise<{ default: Component }>,
): Component {
  return defineAsyncComponent({
    loader,
    suspensible: false,
  });
}

const moduleComponents: Record<string, Component> = {
  ActivityDetailModule: loadModule(
    () => import("@wkt/components/modules/activitydetail/ActivityDetailModule.vue"),
  ),
  AssessmentReviewModule: loadModule(
    () => import("@wkt/components/modules/documents/AssessmentReviewModule.vue"),
  ),
  CheckinModule: loadModule(
    () => import("@wkt/components/modules/checkin/CheckinModule.vue"),
  ),
  CheckinHistoryOverviewModule: loadModule(
    () => import("@wkt/components/modules/checkin/CheckinHistoryOverviewModule.vue"),
  ),
  CheckinHistoryDetailModule: loadModule(
    () => import("@wkt/components/modules/checkin/CheckinHistoryDetailModule.vue"),
  ),
  DocByCategoryModule: loadModule(
    () => import("@wkt/components/modules/documents/DocByCategoryModule.vue"),
  ),
  DocumentsModule: loadModule(
    () => import("@wkt/components/modules/documents/DocumentsModule.vue"),
  ),
  EmployeeCalendar: loadModule(
    () => import("@wkt/components/modules/employeecalendar/EmployeeCalendar.vue"),
  ),
  EmployeeModule: loadModule(
    () => import("@wkt/components/modules/employee/EmployeeModule.vue"),
  ),
  ExpenseSheetDetailDialog: loadModule(
    () => import("@wkt/components/modules/expensesheets/ExpenseSheetDetailDialog.vue"),
  ),
  ExpenseSheetsModule: loadModule(
    () => import("@wkt/components/modules/expensesheets/ExpenseSheetsModule.vue"),
  ),
  FormViewerModule: loadModule(
    () => import("@wkt/components/modules/documents/FormViewerModule.vue"),
  ),
  HolidayCalendarModule: loadModule(
    () => import("@wkt/components/modules/holidaycalendar/HolidayCalendarModule.vue"),
  ),
  HomeModule: loadModule(
    () => import("@wkt/components/modules/home/HomeModule.vue"),
  ),
  KioskModule: loadModule(
    () => import("@wkt/components/modules/kiosk/KioskModule.vue"),
  ),
  NewsModule: loadModule(
    () => import("@wkt/components/modules/news/NewsModule.vue"),
  ),
  ProceduresModule: loadModule(
    () => import("@wkt/components/modules/procedures/ProceduresModule.vue"),
  ),
  ProfileModule: loadModule(
    () => import("@wkt/components/modules/profile/ProfileModule.vue"),
  ),
  PeriodClosureHistoryModule: loadModule(
    () => import("@wkt/components/modules/periodclosure/PeriodClosureHistoryModule.vue"),
  ),
  ProjectsModule: loadModule(
    () => import("@wkt/components/modules/projects/ProjectsModule.vue"),
  ),
  RegisterModule: loadModule(
    () => import("@wkt/components/modules/register/RegisterModule.vue"),
  ),
  SurveyViewerModule: loadModule(
    () => import("@wkt/components/modules/survey/SurveyViewerModule.vue"),
  ),
  TargetHoursModule: loadModule(
    () => import("@wkt/components/modules/targethours/TargetHoursModule.vue"),
  ),
  TrainingModule: loadModule(
    () => import("@wkt/components/modules/training/TrainingModule.vue"),
  ),
  VacationsDetailModule: loadModule(
    () => import("@wkt/components/modules/vacations/VacationsDetailModule.vue"),
  ),
  VacationsModule: loadModule(
    () => import("@wkt/components/modules/vacations/VacationsModule.vue"),
  ),
};

const currentModuleName = computed(
  () => getVueModuleForRouteName(route.name) ?? shellStore.pendingRevealModule,
);

const currentComponent = computed<Component | null>(() => {
  const moduleName = currentModuleName.value;
  return moduleName ? moduleComponents[moduleName] ?? null : null;
});

function resolveRevealPayload(): unknown {
  const moduleName = currentModuleName.value;
  if (!moduleName) {
    return undefined;
  }

  if (shellStore.pendingRevealModule === moduleName) {
    return shellStore.pendingRevealPayload;
  }

  return getRevealPayloadFromRoute(moduleName, route);
}

function getRevealSignature(): string | null {
  const moduleName = currentModuleName.value;
  if (!moduleName) {
    return null;
  }

  return [
    moduleName,
    route.fullPath,
    shellStore.pendingRevealToken,
  ].join("::");
}

async function revealCurrentModule(): Promise<void> {
  const moduleName = currentModuleName.value;
  const revealSignature = getRevealSignature();
  if (revealSignature && revealSignature === lastRevealSignature) {
    return;
  }

  const moduleRef = activeModuleRef.value as
    | (ComponentPublicInstance & { onReveal?: (payload?: unknown) => void })
    | null;

  if (!moduleRef || typeof moduleRef.onReveal !== "function") {
    return;
  }

  const payload = resolveRevealPayload();

  await nextTick();
  await nextTick();

  const settledModuleRef = activeModuleRef.value as
    | (ComponentPublicInstance & { onReveal?: (payload?: unknown) => void })
    | null;

  if (settledModuleRef && typeof settledModuleRef.onReveal === "function") {
    settledModuleRef.onReveal(payload);
    if (revealSignature) {
      lastRevealSignature = revealSignature;
    }
  }

  if (shellStore.pendingRevealModule === moduleName) {
    shellStore.clearPendingReveal(moduleName);
  }
}

async function refreshCurrentModule(): Promise<void> {
  const moduleRef = activeModuleRef.value as
    | (ComponentPublicInstance & { refreshModule?: () => void })
    | null;

  if (!moduleRef || typeof moduleRef.refreshModule !== "function") {
    return;
  }

  await nextTick();
  moduleRef.refreshModule();
}

watch(
  () => currentModuleName.value,
  (moduleName, previousModuleName) => {
    shellStore.setCurrentBridgeModule(moduleName ?? null);
    if (!moduleName || moduleName !== previousModuleName) {
      lastRevealSignature = null;
    }
  },
  { immediate: true },
);

watch(
  () => [route.fullPath, shellStore.pendingRevealToken],
  async () => {
    await revealCurrentModule();
  },
  { immediate: true },
);

watch(
  () => activeModuleRef.value,
  async (moduleRef) => {
    if (!moduleRef) {
      return;
    }

    await revealCurrentModule();
  },
);

watch(
  () => shellStore.refreshToken,
  async () => {
    await refreshCurrentModule();
  },
);

onMounted(async () => {
  await revealCurrentModule();
});
</script>
